/**
 * BarberQueue - Barber queue management page
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useQueue } from '../hooks/useQueue';
import { reorderQueue, getClientPhone } from '../services/queueService';
import { getBarberByUserId } from '../services/barberService';
import type { QueueTicket } from '../types';
import { toast } from '@/lib/toast';
import { db, functions } from '@/lib/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { BarberStatus } from '../types';
import { formatWaitTime } from '@/lib/timeUtils';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Clock, Coffee, Power, Loader2, Baby, PersonStanding, Megaphone, Calendar, Crown, Gift, CheckCircle, Phone } from 'lucide-react';
import { useEstimatedTime } from '../hooks/useEstimatedTime';
import { BarberDashboard } from '../components/barber/BarberDashboard';
import { CompletedTodayList } from '../components/barber/CompletedTodayList';
import { useBarberStats } from '../hooks/useBarberStats';
import { TicketActionsMenu } from '../components/queue/TicketActionsMenu';
import BarberNotificationPanel from '../components/queue/BarberNotificationPanel';
import { AddTimeModal } from '../components/queue/AddTimeModal';
import FinishingSoonModal from '../components/queue/FinishingSoonModal';
import { CallClientModal } from '../components/queue/CallClientModal';
import PaymentModal from '../components/queue/PaymentModal';
import { PendingOvertimeRequests } from '../components/queue/PendingOvertimeRequests';
import { QuickActionsCard } from '../components/queue/QuickActionsCard';
import { AvailabilityModal } from '../components/barber/AvailabilityModal';
import { BreakStatusBanner } from '../components/barber/BreakStatusBanner';
// QueueEstimateDisplay available but not used yet (estimatedWaitTime already shown in table)

/**
 * Sortable Ticket Row Component
 */
interface SortableTicketRowProps {
  ticket: QueueTicket;
  index: number;
  allTickets: QueueTicket[];
  handleCallClient: (ticket?: QueueTicket) => void;
  handleMarkArrival: (queueId: string) => void;
  handleStartService: (queueId: string) => void;
  handleCompleteTicket: (queueId: string) => void;
  handleCancelTicket: (queueId: string) => void;
  handleAddTime: (ticket: QueueTicket) => void;
  processing: boolean;
  getStatusBadge: (status: string) => string;
  getStatusText: (status: string) => string;
}

function SortableTicketRow({
  ticket,
  index,
  allTickets,
  handleCallClient,
  handleMarkArrival,
  handleStartService,
  handleCompleteTicket,
  handleCancelTicket,
  handleAddTime,
  processing,
  getStatusBadge,
  getStatusText,
}: SortableTicketRowProps) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: ticket.queueId });

  // Calculate estimated time for this ticket
  const estimatedTime = useEstimatedTime({
    tickets: allTickets,
    currentPosition: ticket.position,
  });

  // Calculate remaining service time for in_service tickets
  const [remainingServiceMinutes, setRemainingServiceMinutes] = useState<number>(0);

  useEffect(() => {
    if (ticket.status !== 'in_service' || !ticket.serviceStartedAt) {
      setRemainingServiceMinutes(0);
      return;
    }

    const calculateRemainingTime = () => {
      const serviceStartTime = ticket.serviceStartedAt!.toMillis();
      const now = Date.now();
      const elapsedMinutes = Math.floor((now - serviceStartTime) / (1000 * 60));
      // Use serviceDuration (actual service time) NOT estimatedWaitTime (queue wait time)
      const estimatedServiceDuration = ticket.serviceDuration || 30;
      const remaining = Math.max(0, estimatedServiceDuration - elapsedMinutes);
      setRemainingServiceMinutes(remaining);
    };

    // Calculate initially
    calculateRemainingTime();

    // Update every minute
    const interval = setInterval(calculateRemainingTime, 60000);

    return () => clearInterval(interval);
  }, [ticket.status, ticket.serviceStartedAt, ticket.serviceDuration]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`hover:bg-surface-2 transition-colors ${
        index === 0 ? 'bg-accent/5' : ''
      } ${isDragging ? 'shadow-lg' : ''}`}
    >
      {/* Drag Handle */}
      <td className="py-3 px-4">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-ink-subtle hover:text-ink touch-none"
          aria-label="Drag to reorder"
        >
          <GripVertical size={20} />
        </button>
      </td>

      <td className="py-3 px-4">
        <span className={`text-lg font-semibold tabular-nums ${
          (ticket.status === 'in_service' || ticket.status === 'finishing') ? 'text-success' :
          index === 0 ? 'text-accent' : 'text-ink'
        }`}>
          {(ticket.status === 'in_service' || ticket.status === 'finishing') ? '✂' : ticket.position}
        </span>
      </td>

      <td className="py-3 px-4">
        <div className="flex flex-col">
          {/* Show companion name if this is a companion ticket */}
          {ticket.isCompanion && ticket.companionName ? (
            <>
              <span className="font-semibold text-ink text-sm flex items-center gap-1.5">
                {ticket.companionType === 'child'
                  ? <Baby className="w-3.5 h-3.5 text-ink-subtle" aria-hidden="true" />
                  : <PersonStanding className="w-3.5 h-3.5 text-ink-subtle" aria-hidden="true" />}
                {ticket.companionName}
              </span>
              <span className="text-xs text-ink-muted font-medium">
                {t('queueManagement.barberInterface.companionOf')} {ticket.clientName || t('queueManagement.barberInterface.client')}
              </span>
            </>
          ) : (
            <span className="font-semibold text-ink text-sm">
              {ticket.clientName || ticket.clientEmail || t('queueManagement.barberInterface.client')}
            </span>
          )}
          {ticket.clientEmail && ticket.clientName && !ticket.isCompanion && (
            <span className="text-xs text-ink-subtle">
              {ticket.clientEmail}
            </span>
          )}
          <span className="font-mono text-xs text-ink-subtle">
            #{ticket.ticketNumber}
          </span>

          {/* Badges Container */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {/* Appointment Badge */}
            {(ticket as any).isAppointment && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-info/10 text-info border border-info/30">
                <Calendar className="w-3 h-3" aria-hidden="true" />
                <span>{t('queueManagement.barberInterface.appointment')}</span>
              </span>
            )}

            {/* VIP Badge */}
            {(ticket as any).isVIP && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/30">
                <Crown className="w-3 h-3" aria-hidden="true" />
                <span>VIP</span>
              </span>
            )}

            {/* Reward Request Badge - Shows when client requested to use a reward */}
            {ticket.rewardRequest?.status === 'pending' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/30">
                <Gift className="w-3 h-3" aria-hidden="true" />
                <span>{t('queueManagement.barberInterface.pendingVoucher')}</span>
              </span>
            )}
          </div>

          {/* Reward Request Details */}
          {ticket.rewardRequest?.status === 'pending' && (
            <div className="mt-2 p-2 bg-surface-2 border border-accent/30 rounded">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-ink">
                    {t('queueManagement.barberInterface.clientRequestsVoucher')}
                  </p>
                  <p className="text-xs text-ink-muted">
                    {t('queueManagement.barberInterface.code')}: {ticket.rewardRequest.rewardCode}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-ink-subtle">
                    {t('queueManagement.barberInterface.pendingAuth')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Loyalty Reward Badge */}
          {(ticket as any).loyaltyReward && (
            <div className="mt-2">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/30">
                <Gift className="w-3 h-3" aria-hidden="true" />
                <span>{t('queueManagement.barberInterface.rewardApplied')}</span>
              </span>
              <div className="mt-1 text-xs space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-ink-subtle">{t('queueManagement.barberInterface.originalPrice')}:</span>
                  <span className="font-semibold text-ink-muted line-through tabular-nums">
                    {((ticket as any).loyaltyReward.originalPrice || 0).toFixed(2)}€
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-ink-subtle">{t('queueManagement.barberInterface.discount')}:</span>
                  <span className="font-semibold text-success tabular-nums">
                    -{((ticket as any).loyaltyReward.discountAmount || 0).toFixed(2)}€
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-ink-subtle">{t('queueManagement.barberInterface.toCharge')}:</span>
                  <span className="font-bold text-lg text-ink tabular-nums">
                    {((ticket as any).loyaltyReward.finalPrice || 0).toFixed(2)}€
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </td>

      {/* Service column */}
      <td className="py-3 px-4">
        {ticket.serviceName ? (
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-ink">
              {ticket.serviceName}
            </span>
            {ticket.serviceDuration && (
              <span className="text-xs text-ink-subtle tabular-nums">
                {ticket.serviceDuration} min
              </span>
            )}
            {ticket.servicePrice != null && ticket.servicePrice > 0 && (
              <span className="text-xs font-medium text-ink tabular-nums">
                {(ticket.servicePrice / 100).toFixed(2)}€
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-ink-subtle italic">—</span>
        )}
      </td>

      <td className="py-3 px-4">
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(ticket.status)}`}>
          <span>{getStatusText(ticket.status)}</span>
        </span>
      </td>

      <td className="py-3 px-4 text-sm">
        {ticket.status === 'in_service' ? (
          <span className="inline-flex items-center gap-1 font-semibold text-info tabular-nums">
            <Clock className="w-3.5 h-3.5" aria-hidden="true" /> {t('queueManagement.barberInterface.remainingMinutes', { minutes: remainingServiceMinutes })}
          </span>
        ) : (
          <span className="text-ink-muted">
            {formatWaitTime(ticket.estimatedWaitTime || 0)}
          </span>
        )}
      </td>

      <td className="py-3 px-4">
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${
          estimatedTime.isNow
            ? 'bg-accent/10 text-accent border-accent/30'
            : 'bg-surface-2 text-ink-muted border-hairline'
        }`}>
          <Clock size={12} />
          {estimatedTime.isNow ? (
            <span className="font-semibold">Ahora</span>
          ) : (
            <span>{estimatedTime.time}</span>
          )}
        </span>
      </td>

      <td className="py-3 px-4 text-sm text-ink-muted">
        {ticket.createdAt?.toDate().toLocaleTimeString('es-AR', {
          hour: '2-digit',
          minute: '2-digit'
        })}
      </td>

      <td className="py-3 px-4">
        <div className="flex justify-end">
          <TicketActionsMenu
            ticket={ticket}
            onCallClient={() => handleCallClient(ticket)}
            onMarkArrival={() => handleMarkArrival(ticket.queueId)}
            onStartService={() => handleStartService(ticket.queueId)}
            onComplete={() => handleCompleteTicket(ticket.queueId)}
            onCancel={() => handleCancelTicket(ticket.queueId)}
            onAddTime={() => handleAddTime(ticket)}
            disabled={processing}
          />
        </div>
      </td>
    </tr>
  );
}

export default function BarberQueue() {
  const { t } = useTranslation();
  const { user, firebaseUser, customClaims, loading: authLoading } = useAuth();
  const barberBranchId = user?.branchId || customClaims?.branchId;
  const [reordering, setReordering] = useState(false);

  // Modals state
  const [addTimeModalOpen, setAddTimeModalOpen] = useState(false);
  const [addTimeTicket, setAddTimeTicket] = useState<QueueTicket | null>(null);
  const [finishingSoonModalOpen, setFinishingSoonModalOpen] = useState(false);
  const [callClientModalOpen, setCallClientModalOpen] = useState(false);
  const [callClientTicket, setCallClientTicket] = useState<QueueTicket | null>(null);
  const [callClientPhone, setCallClientPhone] = useState<string | null>(null);
  // Phone of the highlighted "next client" — fetched on demand (never stored on
  // the queue doc for privacy) so the barber can call them without opening a modal.
  const [nextClientPhone, setNextClientPhone] = useState<string | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentModalTicket, setPaymentModalTicket] = useState<QueueTicket | null>(null);
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);

  // Availability status state
  const [availabilityStatus, setAvailabilityStatus] = useState<BarberStatus | null>(null);
  const [togglingAvailability, setTogglingAvailability] = useState(false);

  // State for the barber's barberId (document ID in barbers collection)
  const [barberId, setBarberId] = useState<string | undefined>(undefined);
  const [loadingBarberId, setLoadingBarberId] = useState(true);

  // Get the correct barberId from the barbers collection using userId
  useEffect(() => {
    async function fetchBarberId() {
      // Wait for auth to finish loading
      if (authLoading) {
        setLoadingBarberId(true);
        return;
      }

      if (!firebaseUser?.uid) {
        setBarberId(undefined);
        setLoadingBarberId(false);
        return;
      }

      try {
        setLoadingBarberId(true);
        const barber = await getBarberByUserId(firebaseUser.uid);

        if (barber) {
          setBarberId(barber.barberId);
          console.log('✅ Barber found:', {
            userId: firebaseUser.uid,
            barberId: barber.barberId,
            displayName: barber.displayName
          });
        } else {
          console.warn('⚠️ No barber document found for userId:', firebaseUser.uid);
          setBarberId(undefined);
        }
      } catch (error) {
        console.error('❌ Error fetching barber:', error);
        setBarberId(undefined);
      } finally {
        setLoadingBarberId(false);
      }
    }

    fetchBarberId();
  }, [firebaseUser?.uid, authLoading]);

  // DEBUG: Log barber info
  console.log('🔍 BarberQueue DEBUG:', {
    firebaseUserId: firebaseUser?.uid,
    barberId: barberId,
    loadingBarberId: loadingBarberId,
    userBranchId: user?.branchId,
    claimsBranchId: customClaims?.branchId,
    barberBranchId
  });

  // Subscribe to barber availability status
  useEffect(() => {
    if (!barberId) return;

    const statusRef = doc(db, 'barberStatus', barberId);
    const unsubscribe = onSnapshot(statusRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data() as BarberStatus;
        console.log('📡 Barber status update:', { barberId, data });
        setAvailabilityStatus(data);
      } else {
        console.log('📡 No barber status doc, defaulting to available');
        // Default to available if no status document exists
        setAvailabilityStatus({
          barberId,
          status: 'available',
        } as unknown as BarberStatus);
      }
    }, (error) => {
      console.error('Error subscribing to barber status:', error);
    });

    return () => unsubscribe();
  }, [barberId]);

  // Toggle availability handler
  const handleToggleAvailability = async () => {
    if (!barberId || togglingAvailability) return;

    // Debug log
    console.log('🔄 Toggle availability:', {
      barberId,
      currentStatus: availabilityStatus?.status,
      availabilityStatus
    });

    const isCurrentlyAvailable = availabilityStatus?.status === 'available';
    const newStatus = isCurrentlyAvailable ? 'offline' : 'available';

    setTogglingAvailability(true);
    try {
      const setBarberAvailability = httpsCallable(functions, 'setBarberAvailability');
      await setBarberAvailability({
        barberId,
        status: newStatus,
        reason: newStatus === 'offline' ? 'day_off' : undefined,
      });

      toast.success(newStatus === 'offline' ? 'Ahora no estás disponible' : '¡Estás disponible!');
    } catch (error) {
      console.error('Error toggling availability:', error);
      toast.error('Error al cambiar disponibilidad');
    } finally {
      setTogglingAvailability(false);
    }
  };

  const {
    queueTickets,
    queueLoading,
    advanceQueue,
    markArrival,
    startService,
    cancelTicket,
    advancing,
    error
  } = useQueue({
    barberId: barberId, // ✅ Now using correct barberId from barbers collection
    branchId: barberBranchId
  });

  // DEBUG: Log queue state
  console.log('🎫 Queue State:', {
    barberId: barberId,
    ticketsCount: queueTickets.length,
    tickets: queueTickets,
    loading: queueLoading,
    error
  });

  // Track general processing state for actions menu
  const [processing, setProcessing] = useState(false);

  // Local state for tickets to allow optimistic reordering
  const [localTickets, setLocalTickets] = useState<QueueTicket[]>([]);

  // Calculate barber stats for dashboard
  const barberStats = useBarberStats({
    barberId: barberId || '',
    branchId: barberBranchId || '',
    tickets: localTickets.filter(t =>
      t.status === 'waiting' ||
      t.status === 'notified' ||
      t.status === 'arrived' ||
      t.status === 'in_service'
    ),
  });

  // Sync queue tickets with local state
  useEffect(() => {
    setLocalTickets(queueTickets);
  }, [queueTickets]);

  // Setup sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // handleCallNext removed - functionality integrated into handleCallClient

  const handleCallClient = (ticket?: QueueTicket) => {
    // Open modal to select grace minutes
    let target: QueueTicket | null = null;
    if (ticket) {
      target = ticket;
    } else {
      // If no ticket provided, find the next one in line
      target = localTickets.find(t =>
        t.status === 'waiting' || t.status === 'arrived'
      ) || null;
    }
    setCallClientTicket(target);
    // Phone numbers are never stored on queue docs (privacy); always fetch via callable
    setCallClientPhone(null);
    setCallClientModalOpen(true);

    if (target?.queueId) {
      getClientPhone(target.queueId)
        .then(phone => setCallClientPhone(phone))
        .catch(err => {
          console.warn('Failed to load client phone:', err);
        });
    }
  };

  const handleConfirmCallClient = async (graceMinutes: 5 | 10 | 15 | 20) => {
    if (!barberBranchId || !barberId) return;

    try {
      setProcessing(true);
      await advanceQueue({
        branchId: barberBranchId,
        barberId: barberId,
        graceMinutes: graceMinutes
      });
      toast.success(t('toasts.queue.clientCalled'));
    } catch (err) {
      console.error('Failed to call client:', err);
      toast.error(t('toasts.queue.callError'));
    } finally {
      setProcessing(false);
      setCallClientModalOpen(false);
      setCallClientTicket(null);
      setCallClientPhone(null);
    }
  };

  const handleMarkArrival = async (queueId: string) => {
    try {
      setProcessing(true);
      await markArrival({ queueId });
      toast.success(t('toasts.queue.arrivalMarked'));
    } catch (err) {
      console.error('Failed to mark arrival:', err);
      toast.error(t('toasts.queue.arrivalError'));
    } finally {
      setProcessing(false);
    }
  };

  const handleStartService = async (queueId: string) => {
    try {
      setProcessing(true);
      await startService({ queueId });
      toast.success(t('toasts.queue.ticketStarted'));
    } catch (err) {
      console.error('Failed to start service:', err);
      toast.error(t('toasts.queue.startError'));
    } finally {
      setProcessing(false);
    }
  };

  const handleCompleteTicket = async (queueId: string) => {
    try {
      setProcessing(true);

      // Find ticket before completing to get full data
      const ticketToComplete = localTickets.find(t => t.queueId === queueId);

      if (!ticketToComplete) {
        toast.error(t('toasts.queue.ticketNotFound'));
        setProcessing(false);
        return;
      }

      // Open payment modal BEFORE completing - for easier testing
      setPaymentModalTicket(ticketToComplete);
      setPaymentModalOpen(true);
      setProcessing(false);

      // Note: Ticket will be completed when payment is confirmed in the modal
    } catch (err) {
      console.error('Failed to complete ticket:', err);
      toast.error(t('toasts.queue.completeError'));
      setProcessing(false);
    }
  };

  const handleCancelTicket = async (queueId: string) => {
    try {
      setProcessing(true);
      await cancelTicket({ queueId, reason: t('queueManagement.actions.cancelledByBarber') });
      toast.success(t('toasts.queue.ticketCancelled'));
    } catch (err) {
      console.error('Failed to cancel ticket:', err);
      toast.error(t('toasts.queue.cancelError'));
    } finally {
      setProcessing(false);
    }
  };

  // Handlers for modals
  const handleOpenAddTimeModal = (ticket: QueueTicket) => {
    setAddTimeTicket(ticket);
    setAddTimeModalOpen(true);
  };

  const handleOpenFinishingSoonModal = () => {
    setFinishingSoonModalOpen(true);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = localTickets.findIndex(t => t.queueId === active.id);
    const newIndex = localTickets.findIndex(t => t.queueId === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Optimistically update local state
    const reorderedTickets = arrayMove(localTickets, oldIndex, newIndex);
    setLocalTickets(reorderedTickets);

    // Update positions in Firestore
    try {
      setReordering(true);

      // All tickets get sequential 1-based positions
      let pos = 1;
      const ticketPositions = reorderedTickets.map((ticket) => {
        const position = pos++;
        return { queueId: ticket.queueId, position };
      });

      await reorderQueue({ ticketPositions });
      toast.success(t('toasts.queue.reordered'));
    } catch (err) {
      console.error('Failed to reorder queue:', err);
      toast.error(t('toasts.queue.reorderError'));
      // Revert local state on error
      setLocalTickets(queueTickets);
    } finally {
      setReordering(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      waiting: 'bg-accent/10 text-accent border-accent/30',
      notified: 'bg-accent/15 text-accent border-accent/40',
      arrived: 'bg-success/10 text-success border-success/30',
      in_service: 'bg-info/10 text-info border-info/30',
    };
    return badges[status] || 'bg-surface-2 text-ink-muted border-hairline';
  };

  const getStatusText = (status: string) => {
    const key = `queueManagement.barberInterface.statuses.${status}`;
    const translated = t(key);
    return translated !== key ? translated : status;
  };

  // Find next ticket (first waiting, notified, or arrived, sorted by position)
  const nextTicket = localTickets
    .filter(t => t.status === 'waiting' || t.status === 'notified' || t.status === 'arrived')
    .sort((a, b) => a.position - b.position)[0];

  const currentTicket = localTickets.find(t => t.status === 'in_service' || t.status === 'finishing');

  // Load the next client's phone so it shows on the highlighted card (not just
  // inside the "Llamar Cliente" modal). All tickets here are assigned to this
  // barber, so getClientPhone is authorized.
  const nextTicketId = nextTicket?.queueId;
  useEffect(() => {
    let cancelled = false;
    if (!nextTicketId) {
      setNextClientPhone(null);
      return;
    }
    getClientPhone(nextTicketId)
      .then((phone) => {
        if (!cancelled) setNextClientPhone(phone);
      })
      .catch((err) => {
        console.warn('Failed to load next client phone:', err);
        if (!cancelled) setNextClientPhone(null);
      });
    return () => {
      cancelled = true;
    };
  }, [nextTicketId]);

  // Next ticket to notify: first waiting/notified ticket after current service
  // With in_service at position 0, the next in line is position 1
  const nextTicketForNotif = localTickets.find(t =>
    (t.status === 'waiting' || t.status === 'notified') && t.position >= 1
  );

  console.log('🎫 BarberQueue: Tickets state', {
    totalTickets: localTickets.length,
    nextTicket: nextTicket ? {
      id: nextTicket.queueId,
      position: nextTicket.position,
      status: nextTicket.status,
      ticketNumber: nextTicket.ticketNumber,
    } : null,
    currentTicket: currentTicket ? {
      id: currentTicket.queueId,
      status: currentTicket.status,
    } : null,
    allTicketStatuses: localTickets.map(t => ({ position: t.position, status: t.status })),
  });

  return (
    <div className="min-h-screen bg-canvas py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header with Notification Panel */}
        <div className="flex justify-between items-center mb-8 relative">
          <h1 className="font-display text-2xl sm:text-3xl text-ink">{t('queueManagement.barberInterface.title')}</h1>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-ink-muted">{t('queueManagement.barberInterface.barber')}:</span>
              <span className="font-semibold text-ink">{user?.displayName || user?.email}</span>
            </div>
            {/* Availability Button */}
            <button
              onClick={() => setAvailabilityModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-surface-2 border border-hairline hover:bg-surface-3 text-ink-muted hover:text-ink rounded-lg font-medium transition-colors"
              title="Cambiar disponibilidad"
            >
              <Coffee size={18} />
              <span className="hidden sm:inline">Pausa</span>
            </button>
            {/* Notification Panel */}
            {barberId && (
              <BarberNotificationPanel barberId={barberId} />
            )}
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-danger/10 border border-danger/30 text-ink px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Pausa temporal en curso: motivo, cuenta atrás, hora de vuelta y acciones (issue #18) */}
        {barberId && availabilityStatus?.status === 'on_break' && availabilityStatus.breakIsTemporary && (
          <BreakStatusBanner barberId={barberId} status={availabilityStatus} />
        )}

        {/* Big Availability Toggle Button */}
        <div className="mb-6">
          <button
            onClick={handleToggleAvailability}
            disabled={togglingAvailability}
            className={`w-full p-5 rounded-xl text-white font-bold text-xl transition-colors flex items-center justify-center gap-4 ${
              availabilityStatus?.status === 'available'
                ? 'bg-success hover:bg-success/90'
                : availabilityStatus?.status === 'on_break' && availabilityStatus.breakIsTemporary
                  ? 'bg-warning hover:bg-warning/90'
                  : 'bg-danger hover:bg-danger/90'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {togglingAvailability ? (
              <Loader2 className="w-8 h-8 animate-spin" />
            ) : (
              <>
                <Power className="w-8 h-8" />
                <span className="text-2xl">
                  {availabilityStatus?.status === 'available'
                    ? 'DISPONIBLE'
                    : availabilityStatus?.status === 'on_break' && availabilityStatus.breakIsTemporary
                      ? 'EN PAUSA · RESERVABLE'
                      : 'NO DISPONIBLE'}
                </span>
              </>
            )}
          </button>
        </div>

        {/* Barber's Branch Info (solo lectura, sin selector) */}
        {barberBranchId && (
          <>
            {/* Next ticket - ARRIBA para visibilidad rápida del barbero */}
            {nextTicket ? (
              <div className="bg-surface border border-accent/40 rounded-xl p-6 mb-6">
                <div className="flex justify-between items-center gap-4">
                  <div>
                    <p className="text-xs text-ink-subtle uppercase tracking-wide mb-1">{t('queueManagement.barberInterface.nextClient')}</p>
                    <p className="text-4xl font-semibold text-ink tabular-nums mb-2">{nextTicket.ticketNumber}</p>
                    {(nextTicket.isCompanion && nextTicket.companionName) ? (
                      <p className="text-lg font-semibold text-ink mb-1 flex items-center gap-2">
                        {nextTicket.companionType === 'child'
                          ? <Baby className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
                          : <PersonStanding className="w-4 h-4 text-ink-subtle" aria-hidden="true" />}
                        {nextTicket.companionName}
                        <span className="text-sm font-normal text-ink-muted">({t('queueManagement.barberInterface.companion')})</span>
                      </p>
                    ) : nextTicket.clientName && (
                      <p className="text-lg font-semibold text-ink mb-1">
                        {nextTicket.clientName}
                      </p>
                    )}
                    {nextClientPhone && (
                      <a
                        href={`tel:${nextClientPhone}`}
                        className="inline-flex items-center gap-1.5 text-base font-medium text-accent hover:underline mb-1"
                      >
                        <Phone className="w-4 h-4" aria-hidden="true" />
                        {nextClientPhone}
                      </a>
                    )}
                    {nextTicket.serviceName && (
                      <p className="text-sm font-medium text-ink-muted mb-1">
                        {nextTicket.serviceName}
                        {nextTicket.serviceDuration ? ` · ${nextTicket.serviceDuration} min` : ''}
                      </p>
                    )}
                    <p className="text-sm text-ink-muted">
                      {t('queueManagement.barberInterface.position')} {nextTicket.position} · {getStatusText(nextTicket.status)}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    {/* Finishing Soon Button (only show if there's a current client in service) */}
                    {currentTicket && nextTicketForNotif && (
                      <button
                        onClick={handleOpenFinishingSoonModal}
                        className="bg-surface-2 border border-hairline hover:bg-surface-3 text-ink px-6 py-4 rounded-lg font-semibold text-lg transition-colors flex items-center gap-2"
                      >
                        <Clock size={20} />
                        {t('queueManagement.barberInterface.finishingSoon')}
                      </button>
                    )}
                    <button
                      onClick={() => handleCallClient(nextTicket)}
                      disabled={advancing || processing}
                      className="bg-accent text-on-accent px-8 py-4 rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed font-bold text-lg transition-colors inline-flex items-center gap-2"
                    >
                      {advancing || processing
                        ? t('queueManagement.barberInterface.calling')
                        : <><Megaphone className="w-5 h-5" aria-hidden="true" /> {t('queueManagement.barberInterface.callClient')}</>}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-surface border border-hairline rounded-xl p-6 mb-6 text-center">
                <CheckCircle className="w-8 h-8 text-ink-subtle mx-auto mb-2" aria-hidden="true" />
                <p className="text-lg font-semibold text-ink">{t('queueManagement.barberInterface.emptyQueue')}</p>
              </div>
            )}

            {/* Queue list */}
            <div className="bg-surface border border-hairline rounded-xl p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-sans text-lg font-semibold text-ink">
                  {t('queueManagement.barberInterface.fullQueue')} ({localTickets.length})
                </h2>
                {reordering && (
                  <span className="text-sm text-ink-muted flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    {t('queueManagement.barberInterface.savingOrder')}
                  </span>
                )}
              </div>

              {queueLoading ? (
                <p className="text-sm text-ink-muted">{t('queueManagement.barberInterface.loadingQueue')}</p>
              ) : localTickets.length === 0 ? (
                <p className="text-sm text-ink-muted text-center py-8">{t('queueManagement.barberInterface.noTickets')}</p>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-surface-2 border-b border-hairline">
                        <tr>
                          <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-wide text-ink-subtle w-12">
                            <span className="sr-only">{t('queueManagement.barberInterface.tableHeaders.drag')}</span>
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-wide text-ink-subtle">{t('queueManagement.barberInterface.tableHeaders.pos')}</th>
                          <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-wide text-ink-subtle">{t('queueManagement.barberInterface.tableHeaders.ticket')}</th>
                          <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-wide text-ink-subtle">{t('queueManagement.barberInterface.tableHeaders.service')}</th>
                          <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-wide text-ink-subtle">{t('queueManagement.barberInterface.tableHeaders.status')}</th>
                          <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-wide text-ink-subtle">{t('queueManagement.barberInterface.tableHeaders.wait')}</th>
                          <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-wide text-ink-subtle">{t('queueManagement.barberInterface.tableHeaders.estTime')}</th>
                          <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-wide text-ink-subtle">{t('queueManagement.barberInterface.tableHeaders.created')}</th>
                          <th className="text-right py-3 px-4 font-medium text-xs uppercase tracking-wide text-ink-subtle">{t('queueManagement.barberInterface.tableHeaders.actions')}</th>
                        </tr>
                      </thead>
                      <SortableContext
                        items={localTickets.map(t => t.queueId)}
                        strategy={verticalListSortingStrategy}
                      >
                        <tbody className="divide-y divide-hairline">
                          {localTickets.map((ticket, index) => (
                            <SortableTicketRow
                              key={ticket.queueId}
                              ticket={ticket}
                              index={index}
                              allTickets={localTickets}
                              handleCallClient={handleCallClient}
                              handleMarkArrival={handleMarkArrival}
                              handleStartService={handleStartService}
                              handleCompleteTicket={handleCompleteTicket}
                              handleCancelTicket={handleCancelTicket}
                              handleAddTime={handleOpenAddTimeModal}
                              processing={processing}
                              getStatusBadge={getStatusBadge}
                              getStatusText={getStatusText}
                            />
                          ))}
                        </tbody>
                      </SortableContext>
                    </table>
                  </div>
                </DndContext>
              )}
            </div>

            {/* Quick Actions Card - Acciones rápidas */}
            <div className="mb-8">
              <QuickActionsCard
                currentTicket={currentTicket || null}
                nextTicket={nextTicket || null}
                onCallClient={handleCallClient}
                onMarkArrival={handleMarkArrival}
                onStartService={handleStartService}
                onCompleteTicket={handleCompleteTicket}
                onFinishingSoon={handleOpenFinishingSoonModal}
                processing={processing || advancing}
              />
            </div>

            {/* Dashboard Stats - Debajo de acciones rápidas */}
            <div className="mb-8">
              <BarberDashboard stats={barberStats} />
            </div>

            {/* Pending Overtime Requests */}
            <div className="mb-8">
              {barberId && <PendingOvertimeRequests branchId={barberBranchId} barberId={barberId} />}
            </div>

            {/* Completed Today Section - Al final */}
            {barberId && (
              <CompletedTodayList barberId={barberId} />
            )}
          </>
        )}

        {/* Modals */}
        {addTimeTicket && (
          <AddTimeModal
            ticket={addTimeTicket}
            isOpen={addTimeModalOpen}
            onClose={() => {
              setAddTimeModalOpen(false);
              setAddTimeTicket(null);
            }}
            onSuccess={() => {
              toast.success(t('toasts.queue.timeAdded'));
            }}
          />
        )}

        {currentTicket && (
          <FinishingSoonModal
            currentTicket={currentTicket}
            nextTicket={nextTicketForNotif}
            isOpen={finishingSoonModalOpen}
            onClose={() => setFinishingSoonModalOpen(false)}
            onSuccess={() => {
              toast.success(t('toasts.queue.nextNotified'));
            }}
          />
        )}

        <CallClientModal
          isOpen={callClientModalOpen}
          onClose={() => {
            setCallClientModalOpen(false);
            setCallClientTicket(null);
            setCallClientPhone(null);
          }}
          onConfirm={handleConfirmCallClient}
          onStartServiceDirectly={callClientTicket ? async () => {
            try {
              setProcessing(true);
              // First mark arrival (changes status to 'arrived'), then start service
              await markArrival({ queueId: callClientTicket.queueId });
              await startService({ queueId: callClientTicket.queueId });
              toast.success(t('toasts.queue.ticketStarted'));
              setCallClientModalOpen(false);
              setCallClientTicket(null);
            } catch (err) {
              console.error('Failed to start service directly:', err);
              toast.error(t('toasts.queue.startError'));
            } finally {
              setProcessing(false);
            }
          } : undefined}
          ticketNumber={callClientTicket?.ticketNumber}
          clientName={callClientTicket?.clientName}
          clientPhone={callClientPhone}
          isCompanion={callClientTicket?.isCompanion}
          companionName={callClientTicket?.companionName}
          companionType={callClientTicket?.companionType}
        />

        {paymentModalOpen && paymentModalTicket && (
          <PaymentModal
            ticket={paymentModalTicket}
            onClose={() => {
              setPaymentModalOpen(false);
              setPaymentModalTicket(null);
            }}
            onSuccess={() => {
              // Payment recorded successfully, data will refresh automatically
            }}
          />
        )}

        {/* Availability Modal */}
        <AvailabilityModal
          isOpen={availabilityModalOpen}
          onClose={() => setAvailabilityModalOpen(false)}
          barberId={barberId}
          currentStatus={availabilityStatus?.status === 'busy' ? 'available' : (availabilityStatus?.status ?? 'available')}
          currentReason={availabilityStatus?.breakReason}
          currentReturnTime={availabilityStatus?.breakExpectedEndAt ? availabilityStatus.breakExpectedEndAt.toDate() : null}
          onStatusChanged={() => {
            // Refresh will happen automatically via listeners
          }}
        />
      </div>
    </div>
  );
}
